/**
 * BracketTeam - Single team row within a matchup cell.
 *
 * Shows seed badge (with colored background), team logo, name,
 * projected score, and pick indicator. Clickable to select as winner.
 */

function BracketTeam({ entry, isWinner, isEliminated, projectedScore, isFavored, onClick, isFinal = false }) {
  if (!entry) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 bg-bg-primary/20">
        <span className="w-6 h-5 rounded text-[10px] font-bold flex items-center justify-center bg-bg-card-hover text-text-tertiary">
          --
        </span>
        <span className="text-xs text-text-tertiary italic">TBD</span>
      </div>
    );
  }

  const { bg: seedBg, text: seedText } = getSeedStyle(entry.seed);

  return (
    <button
      onClick={() => onClick?.(entry)}
      disabled={!onClick}
      className={`
        group w-full flex items-center gap-2 px-3 py-2.5 transition-all duration-150
        ${isWinner
          ? 'bg-accent-blue/10'
          : isEliminated
            ? 'bg-bg-primary/10 opacity-35'
            : 'hover:bg-accent-blue/5'
        }
        ${onClick ? 'cursor-pointer' : 'cursor-default'}
      `}
    >
      {/* Seed badge */}
      <span className={`w-6 h-5 rounded text-[10px] font-bold flex items-center justify-center shrink-0 ${seedBg} ${seedText}`}>
        {entry.seed}
      </span>

      {/* Logo */}
      {entry.team_logo_url ? (
        <img
          src={entry.team_logo_url}
          alt=""
          className="w-5 h-5 object-contain shrink-0"
          loading="lazy"
        />
      ) : (
        <div className="w-5 h-5 rounded-full bg-bg-card-hover shrink-0 flex items-center justify-center">
          <span className="text-[8px] font-bold text-text-tertiary">{entry.team_abbreviation?.[0]}</span>
        </div>
      )}

      {/* Team name */}
      <span className={`text-xs font-medium truncate flex-1 text-left ${
        isWinner ? 'text-accent-blue font-semibold' : isEliminated ? 'text-text-tertiary' : 'text-text-primary'
      }`}>
        {entry.team_abbreviation || entry.team_name}
      </span>

      {/* Score: actual (bold) for final games, projected (dim) for upcoming */}
      {projectedScore != null && (
        <span className={`font-mono font-bold tabular-nums shrink-0 ${
          isFinal
            ? isWinner ? 'text-[12px] text-text-primary' : 'text-[11px] text-text-tertiary/50'
            : isFavored ? 'text-[11px] text-accent-blue' : 'text-[11px] text-text-tertiary/40'
        }`}>
          {projectedScore}
        </span>
      )}

      {/* Winner indicator or click hint */}
      {isWinner ? (
        <svg className="w-3.5 h-3.5 text-accent-blue shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ) : onClick && !isEliminated ? (
        <span className="text-[9px] text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          Pick
        </span>
      ) : null}
    </button>
  );
}

function getSeedStyle(seed) {
  if (seed <= 4) return { bg: 'bg-emerald-500/15', text: 'text-emerald-400' };
  if (seed <= 8) return { bg: 'bg-blue-500/15', text: 'text-blue-400' };
  if (seed <= 12) return { bg: 'bg-amber-500/15', text: 'text-amber-400' };
  return { bg: 'bg-red-500/15', text: 'text-red-400' };
}

export default BracketTeam;
